<template>
	<div class="file-upload">
		<b-row>
		<b-col>
			<label>{{ label }}</label>
			<div
				class="file-upload-container d-flex flex-column justify-content-center align-items-center text-center" 
				:style="loading ? '' : 'gap: 8px'"
				@dragover.prevent="onDragOver" 
				@dragleave.prevent="onDragLeave" 
				@drop.prevent="onDrop" 
				@click="triggerFileInput"
				:class="{ 'border-primary': isDragging, 'filled': file }"
			>
        <div v-if="loading" class="d-flex align-items-center">
          <b-spinner small class="mr-2" label="Small Spinner"></b-spinner>
          <p>Carregando...</p>
        </div>
				<input 
					type="file"
          class="d-none" 
					ref="fileInput" 
					@change="onFileChange" 
					:accept="extensions.join(',')" 
				/>
				<div class="d-flex">
					<div v-if="(!file && !loading)" class="d-flex flex-column align-items-center" style="gap: 8px;">
						<img width="32" height="32" src="@/assets/img/icons/document-upload.svg" alt="">
						<div>
							<p class="select-file">{{ $t('views.upload_files.text_1000') }}</p>
							<p class="text-default">{{ $t('views.upload_files.text_1001') }}</p>
						</div>
						<span>
							{{ extensions.join(', ').toUpperCase() }}
							({{ $t('views.upload_files.text_1002') }} {{ maxSize }} {{ sizeUnit.toUpperCase() }}).
						</span>
					</div>
					<div v-if="(file && !loading)"  class="d-flex" style="gap: 16px;">
						<img width="20" height="20" src="@/assets/img/icons/document.svg" alt="">
						<div class="file-info">
							<p class="text-left medium">{{ file.name }}</p>
							<p v-if="file.size" class="text-left regular">{{ readableFileSize(file.size) }}</p>
						</div>
					</div>
				</div>
			</div>
			<p v-if="fileErrorMessage || errorMessage" class="mt-2" style="font-size: 0.875em; color: #ff0c37">{{ fileErrorMessage || errorMessage }}</p>
		</b-col>

		<b-col class="d-flex flex-column justify-content-center">
			<div class="instructions d-flex flex-column justify-content-center" style="gap: 8px; margin-top: 7px;">
				<p class="instructions-label">{{ instructionsLabel }}</p>
				<div v-for="instruction in instructions" class="instruction-item d-flex align-items-center" style="gap: 8px">
					<div style="min-width: 16px;">
						<img :src="require(`@/assets/img/icons/${instruction.icon}`)">
					</div>
					<p>{{ instruction.label }}</p>
				</div>
			</div>
		</b-col>
	</b-row>
	</div>
</template>

<script>
export default {
	props: {
		value: {
      type: [File, String, null], // Permite File, String ou null
      default: null,
    },
		extensions: {
      type: Array,
      default: () => ['jpg', 'png', 'pdf', 'docx'],
    },
		maxSize: {
      type: Number,
      default: 2
    },
		sizeUnit: {
      type: String,
      default: 'mb',
      validator: (value) => ['kb', 'mb', 'gb'].includes(value),
    },
		label: {
			type: String
		},
		instructionsLabel: {
			type: String
		},
		instructions: {
			type: Array
		},
		errorMessage: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false
    }
	},

	data() {
		return {
			isDragging: false,
      fileErrorMessage: '',
			file: null
		};
	},
	watch: {
    value(newFile) {
      this.file = newFile;
    },
    file(newFile) {
      this.$emit("input", newFile);
    },
  },
	computed: {
		maxSizeInBytes() {
      const units = {
        kb: 1024,
        mb: 1024 * 1024,
        gb: 1024 * 1024 * 1024,
      };
      return this.maxSize * units[this.sizeUnit.toUpperCase()];
    },
	},
	methods: {
		triggerFileInput() {
			this.$refs.fileInput.click();
		},
		onFileChange(event) {
			const file = event.target.files[0];
			this.validateFile(file);
		},
		onDragOver() {
			this.isDragging = true;
		},
		onDragLeave() {
			this.isDragging = false;
		},
		onDrop(event) {
			this.isDragging = false;
			const file = event.dataTransfer.files[0];
			this.validateFile(file);
		},
		validateFile(file) {
      const extension = file.name.split('.').pop().toLowerCase();
      const allowedExtensions = this.extensions.map(ext => ext.replace('.', '').toLowerCase());

      if (!allowedExtensions.includes(extension)) {
        this.fileErrorMessage = `${this.$t('views.upload_files.text_1005')}: ${this.extensions.join(', ')}`;
        this.$emit('file-invalid', this.fileErrorMessage);
        this.resetFile();
        return;
      }

      if (file.size > this.maxSizeInBytes) {
        this.fileErrorMessage = `${this.$t('views.upload_files.text_1006')} ${this.maxSize} MB.`;
        this.$emit('file-invalid', this.fileErrorMessage);
        this.resetFile();
        return;
      }

      this.fileErrorMessage = '';
      this.file = file;

      this.$emit('file-selected', file);
      this.$emit('input', file);
    },
    resetFile() {
      this.file = null;
      this.$emit('input', null);
    },
		readableFileSize(fileSize) {
      if (fileSize === null) {
				return '';
			}
      const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];

      let unitIndex = 0;

      while (fileSize >= 1024 && unitIndex < units.length - 1) {
        fileSize /= 1024;
        unitIndex++;
      }
      return `${fileSize.toFixed(2)} ${units[unitIndex]}`;
    },
	},
};
</script>

<style scoped>
.file-upload label {
	font-size: 14px;
	font-style: normal;
	font-weight: 500 !important;
	line-height: 20px;
	color: var(--Default-gray-900, #111827);
	margin-bottom: 7px !important;
}

.file-upload-container {
	border-radius: 8px;
	cursor: pointer;
	transition: border-color 0.3s, background-color 0.3s;
	padding: 24px;
	border-radius: 8px;
	border: 1px dashed var(--Default-gray-400, #9CA3AF);
	background: var(--Default-white, #FFF);
}

.file-upload-container.filled {
	border: 1px solid var(--Default-primary-800, #0854C5);
	padding: 16px !important;
	align-items: flex-start !important;
}

.file-upload-container.border-primary {
	border-color: #0854C5;
	background-color: #e7f3ff;
}

.file-upload-container p.text-default {
	color: var(--Gray-600, #475467) !important;
}

.file-upload-container span {
	color: var(--Gray-600, #475467) !important;
	text-align: center;
	font-size: 12px !important;
	font-style: normal;
	font-weight: 400;
	line-height: 18px !important;
}

.file-upload-container p {
	font-size: 14px !important;
	font-style: normal;
	font-weight: 400;
	line-height: 20px;
	color: var(--Gray-600, #475467) !important;
}

.file-upload-container p.regular {
	font-weight: 400;
	line-height: 20px;
}

.file-upload-container p.medium {
	color: var(--Gray-700, #344054) !important;
	font-weight: 500;
}

.file-upload-container p.select-file {
	color: var(--Default-primary-800, #0854C5) !important;
	font-style: normal;
	font-weight: 600;
	font-size: 14px !important;
}

.file-upload .instructions-label {
  color: var(--Default-gray-900, #111827) !important;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
}

.file-upload .instruction-item p {
  color: var(--Default-gray-900, #111827) !important;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
</style>